import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, CircleDashed, Info, PlugZap, XCircle } from "lucide-react";
import { toast } from "../../../../utils/toast";

import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { Input } from "../../../../components/ui/Input";
import { Select } from "../../../../components/ui/Select";
import { Switch } from "../../../../components/ui/Switch";
import {
  getBsaleCatalog,
  getBsaleConnection,
  saveBsaleConnection,
  testBsaleConnection,
} from "../../../../api/bsale";
import { resolveOperatorCountry } from "./invoicing/fiscalCopy";

const ENVIRONMENTS = [
  { value: "production", label: "Producción" },
  { value: "sandbox", label: "Pruebas (sandbox)" },
];

const fecha = (v) =>
  v ? new Date(v).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—";

/**
 * Conexión del operador con su cuenta Bsale (solo Chile: no hay evidencia oficial de
 * que Bsale emita comprobantes en Argentina).
 *
 * El token nunca se muestra ni se prefilla: el backend lo guarda cifrado y solo
 * informa `tokenSet`. Un token o una sucursal nuevos se verifican contra Bsale
 * antes de guardarse, así que un error al guardar ya trae el motivo.
 *
 * Por ahora la sección conecta y verifica la cuenta; la emisión de facturas a los
 * comercios todavía no existe y la copy lo dice para no prometerla.
 */
export default function BsaleSection({ company, loadingCompany }) {
  const { t } = useTranslation();
  const country = resolveOperatorCountry(company);

  const [conn, setConn] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [replacingToken, setReplacingToken] = useState(false);
  const [officeDraft, setOfficeDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadCatalog = useCallback(async () => {
    setCatalogError(null);
    const r = await getBsaleCatalog();
    if (r?.error) {
      // 404 = sin cuenta conectada o desactivada: no es un fallo que mostrar.
      setCatalog(null);
      if (r.status !== 404) setCatalogError(r.error);
      return;
    }
    setCatalog(r);
  }, []);

  const applyConnection = useCallback(
    (next) => {
      setConn(next);
      setOfficeDraft(next?.officeId ? String(next.officeId) : "");
      if (next?.environment) setEnvironment(next.environment);
      if (next?.configured && next?.isActive) loadCatalog();
      else setCatalog(null);
    },
    [loadCatalog],
  );

  const load = useCallback(async () => {
    setLoadError(null);
    const r = await getBsaleConnection();
    if (r?.error) {
      setLoadError(r.error);
      return;
    }
    applyConnection(r);
  }, [applyConnection]);

  useEffect(() => {
    load();
  }, [load]);

  async function connect(e) {
    e.preventDefault();
    const accessToken = tokenInput.trim();
    if (!accessToken) return;
    setSaving(true);
    const r = await saveBsaleConnection({ accessToken, environment });
    setSaving(false);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    setTokenInput("");
    setReplacingToken(false);
    setTestResult(null);
    toast.success(t("Cuenta Bsale conectada"));
    applyConnection(r);
  }

  async function saveOffice() {
    setSaving(true);
    const r = await saveBsaleConnection({ officeId: Number(officeDraft) });
    setSaving(false);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    toast.success(t("Sucursal guardada"));
    applyConnection(r);
  }

  async function saveTestMode(body) {
    setSaving(true);
    const r = await saveBsaleConnection(body);
    setSaving(false);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    toast.success(body.testMode ? t("Modo de prueba activado") : t("Modo de prueba desactivado"));
    applyConnection(r);
  }

  async function toggleActive(next) {
    setSaving(true);
    const r = await saveBsaleConnection({ isActive: next });
    setSaving(false);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    toast.success(next ? t("Conexión con Bsale activada") : t("Conexión con Bsale desactivada"));
    applyConnection(r);
  }

  async function runTest() {
    setTesting(true);
    const r = await testBsaleConnection();
    setTesting(false);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    setTestResult(r);
    if (r.catalog) setCatalog(r.catalog);
    // El backend guardó el resultado en `lastCheck`: se relee para mostrarlo.
    const fresh = await getBsaleConnection();
    if (!fresh?.error) setConn(fresh);
  }

  if (loadingCompany) {
    return <p className="text-sm text-fg-muted">{t("Cargando…")}</p>;
  }

  if (country && country !== "CL") {
    return (
      <section className="flex flex-col gap-3">
        <SectionHeader t={t} />
        <div className="rounded-lg border border-dashed border-border p-6 text-sm text-fg-muted">
          {t("La conexión con Bsale está disponible solo para operadores de Chile.")}
        </div>
      </section>
    );
  }

  const showTokenForm = conn && (!conn.tokenSet || replacingToken);
  const officeChanged = officeDraft !== (conn?.officeId ? String(conn.officeId) : "");

  return (
    <section className="flex flex-col gap-5">
      <SectionHeader t={t} />

      <div className="flex gap-2 rounded-lg border border-status-info/30 bg-status-info-soft/40 p-3 text-sm text-fg">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-status-info" aria-hidden />
        <p>
          {t(
            "Por ahora puedes conectar y verificar tu cuenta. La emisión automática de facturas a tus comercios se habilitará en una próxima etapa.",
          )}
        </p>
      </div>

      {loadError ? (
        <div className="rounded-lg border border-status-error/40 bg-status-error-soft/40 p-4 text-sm">
          <p className="text-fg">{loadError}</p>
          <Button className="mt-3" variant="secondary" size="sm" onClick={load}>
            {t("Reintentar")}
          </Button>
        </div>
      ) : !conn ? (
        <p className="text-sm text-fg-muted">{t("Cargando…")}</p>
      ) : (
        <>
          <div className="rounded-lg border border-border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-fg">{t("Cuenta Bsale")}</p>
                <ConnectionBadge conn={conn} t={t} />
                {conn.testMode && <Badge tone="warning">{t("Modo de prueba")}</Badge>}
              </div>
              {conn.configured && (
                <Switch
                  checked={conn.isActive}
                  onChange={toggleActive}
                  disabled={saving}
                  label={t("Conexión activa")}
                />
              )}
            </div>

            {conn.configured && (
              <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-fg-muted">{t("Ambiente")}</dt>
                  <dd className="text-fg">
                    {t(ENVIRONMENTS.find((e) => e.value === conn.environment)?.label ?? "—")}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-fg-muted">{t("Token")}</dt>
                  <dd className="flex flex-wrap items-center gap-2 text-fg">
                    {conn.tokenSet
                      ? t("Guardado el {{fecha}}", { fecha: fecha(conn.tokenUpdatedAt) })
                      : t("Sin token")}
                    {conn.tokenSet && !replacingToken && (
                      <Button variant="ghost" size="sm" onClick={() => setReplacingToken(true)}>
                        {t("Reemplazar token")}
                      </Button>
                    )}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs text-fg-muted">{t("Última verificación")}</dt>
                  <dd className="text-fg">
                    {conn.lastCheck
                      ? `${fecha(conn.lastCheck.at)} · ${conn.lastCheck.ok ? t("Correcta") : t("Falló")}`
                      : t("Nunca")}
                  </dd>
                </div>
              </dl>
            )}

            {showTokenForm && (
              <form onSubmit={connect} className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
                <div className="flex flex-col gap-1">
                  <FieldLabel htmlFor="bsale-access-token">{t("Access token de Bsale")}</FieldLabel>
                  <Input
                    id="bsale-access-token"
                    type="password"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder={t("Pega aquí el access_token de tu cuenta")}
                    autoComplete="new-password"
                    spellCheck={false}
                  />
                  <span className="text-xs text-fg-muted">
                    {t(
                      "Se guarda cifrado y no se vuelve a mostrar. Si no lo tienes, solicítalo al soporte de Bsale.",
                    )}
                  </span>
                </div>
                <Select
                  label={t("Ambiente")}
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  options={ENVIRONMENTS.map((e) => ({ value: e.value, label: t(e.label) }))}
                  hint={t("El ambiente de pruebas de Bsale no emite documentos electrónicos.")}
                />
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" loading={saving} disabled={saving || !tokenInput.trim()}>
                    {conn.tokenSet ? t("Guardar token nuevo") : t("Conectar cuenta")}
                  </Button>
                  {replacingToken && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setReplacingToken(false);
                        setTokenInput("");
                      }}
                    >
                      {t("Cancelar")}
                    </Button>
                  )}
                </div>
              </form>
            )}
          </div>

          {conn.configured && conn.isActive && (
            <div className="rounded-lg border border-border p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-medium text-fg">{t("Qué tiene tu cuenta para facturar")}</p>
                <Button variant="secondary" size="sm" onClick={runTest} loading={testing} disabled={testing}>
                  <PlugZap className="h-4 w-4" />
                  {t("Probar conexión")}
                </Button>
              </div>

              {testResult && !testResult.ok && (
                <p className="mt-3 rounded bg-status-error-soft/40 p-2 text-sm text-fg" role="alert">
                  {testResult.message ?? t("La prueba de conexión falló.")}
                </p>
              )}

              {catalogError && (
                <p className="mt-3 text-sm text-status-error" role="alert">
                  {catalogError}
                </p>
              )}

              {catalog && (
                <>
                  <ul className="mt-3 flex flex-col gap-2 text-sm">
                    <CatalogRow
                      t={t}
                      label={t("Factura electrónica (33)")}
                      item={catalog.documentTypes?.invoice}
                      required
                    />
                    <CatalogRow
                      t={t}
                      label={t("Nota de crédito electrónica (61)")}
                      item={catalog.documentTypes?.creditNote}
                      required
                    />
                    <CatalogRow
                      t={t}
                      label={t("Factura exenta (34)")}
                      item={catalog.documentTypes?.exemptInvoice}
                    />
                    <CatalogRow
                      t={t}
                      label={t("IVA")}
                      item={catalog.ivaTax ? { name: `${catalog.ivaTax.name} (${catalog.ivaTax.percentage} %)` } : null}
                      required
                    />
                  </ul>

                  {catalog.warnings?.length > 0 && (
                    <ul className="mt-3 flex flex-col gap-1 rounded-lg border border-status-warning/40 bg-status-warning-soft/40 p-3 text-sm text-fg">
                      {catalog.warnings.map((w) => (
                        <li key={w} className="flex gap-2">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning" aria-hidden />
                          {w}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
                    <div className="min-w-[220px] flex-1">
                      <Select
                        label={t("Sucursal desde la que se emite")}
                        value={officeDraft}
                        onChange={(e) => setOfficeDraft(e.target.value)}
                        placeholder={t("Sucursal por defecto de Bsale")}
                        options={(catalog.offices ?? []).map((o) => ({ value: String(o.id), label: o.name }))}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      onClick={saveOffice}
                      loading={saving}
                      disabled={saving || !officeChanged || !officeDraft}
                    >
                      {t("Guardar sucursal")}
                    </Button>
                  </div>

                  {/* Un backend sin modo de prueba no manda `testDocumentTypes`: se oculta. */}
                  {Array.isArray(catalog.testDocumentTypes) && (
                    <TestModePanel
                      t={t}
                      conn={conn}
                      catalog={catalog}
                      saving={saving}
                      onSave={saveTestMode}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SectionHeader({ t }) {
  return (
    <header>
      <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
        <PlugZap className="h-5 w-5 text-fg-muted" />
        {t("Bsale")}
      </h2>
      <p className="mt-1 text-sm text-fg-muted">
        {t("Conecta tu cuenta Bsale para facturar a tus comercios con tu propia facturación electrónica.")}
      </p>
    </header>
  );
}

/**
 * Modo de prueba: Bsale no tiene forma de probar documentos electrónicos sin folios
 * reales, así que se emite con tipos NO electrónicos de la propia cuenta (que el
 * operador crea en Bsale) y sin declarar al SII. El backend rechaza un tipo electrónico.
 */
function TestModePanel({ t, conn, catalog, saving, onSave }) {
  const invoiceTypes = catalog.testDocumentTypes.filter((d) => !d.isCreditNote);
  const creditNoteTypes = catalog.testDocumentTypes.filter((d) => d.isCreditNote);
  const [enabled, setEnabled] = useState(Boolean(conn.testMode));
  const [invoiceId, setInvoiceId] = useState(conn.testDocumentTypeId ? String(conn.testDocumentTypeId) : "");
  const [creditNoteId, setCreditNoteId] = useState(
    conn.testCreditNoteDocumentTypeId ? String(conn.testCreditNoteDocumentTypeId) : "",
  );

  const changed =
    enabled !== Boolean(conn.testMode) ||
    (enabled &&
      (invoiceId !== String(conn.testDocumentTypeId ?? "") ||
        creditNoteId !== String(conn.testCreditNoteDocumentTypeId ?? "")));
  const canSave = changed && (!enabled || invoiceId);

  function save() {
    if (!enabled) {
      onSave({ testMode: false });
      return;
    }
    onSave({
      testMode: true,
      testDocumentTypeId: Number(invoiceId),
      ...(creditNoteId ? { testCreditNoteDocumentTypeId: Number(creditNoteId) } : {}),
    });
  }

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
      <Switch
        checked={enabled}
        onChange={setEnabled}
        disabled={saving}
        label={t("Modo de prueba")}
      />
      <p className="text-xs text-fg-muted">
        {t(
          "Emite con tipos de documento NO electrónicos de tu cuenta y sin declarar al SII, para probar sin gastar folios. Crea en Bsale un tipo \"Factura\" (y una \"Nota de crédito\") no electrónicos y elígelos aquí.",
        )}
      </p>
      {enabled &&
        (invoiceTypes.length === 0 ? (
          <p className="text-sm text-status-warning" role="alert">
            {t("Tu cuenta Bsale no tiene tipos de documento no electrónicos. Crea uno en Bsale y vuelve a cargar esta página.")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label={t("Factura de prueba")}
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              placeholder={t("Elige un tipo no electrónico")}
              options={invoiceTypes.map((d) => ({ value: String(d.id), label: d.name }))}
            />
            <Select
              label={t("Nota de crédito de prueba (opcional)")}
              value={creditNoteId}
              onChange={(e) => setCreditNoteId(e.target.value)}
              placeholder={t("Sin nota de crédito de prueba")}
              options={creditNoteTypes.map((d) => ({ value: String(d.id), label: d.name }))}
            />
          </div>
        ))}
      <div>
        <Button variant="secondary" onClick={save} loading={saving} disabled={saving || !canSave}>
          {t("Guardar modo de prueba")}
        </Button>
      </div>
    </div>
  );
}

function ConnectionBadge({ conn, t }) {
  if (!conn.configured || !conn.tokenSet) return <Badge tone="neutral">{t("Sin conectar")}</Badge>;
  if (!conn.isActive) return <Badge tone="muted">{t("Desactivada")}</Badge>;
  if (conn.lastCheck && !conn.lastCheck.ok) return <Badge tone="error">{t("Con problemas")}</Badge>;
  return <Badge tone="success">{t("Conectada")}</Badge>;
}

function CatalogRow({ t, label, item, required = false }) {
  const Icon = item ? CheckCircle2 : required ? XCircle : CircleDashed;
  const color = item ? "text-status-success" : required ? "text-status-error" : "text-fg-muted";
  return (
    <li className="flex items-start gap-2">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${color}`} aria-hidden />
      <span className="text-fg">
        {label}
        <span className="text-fg-muted">
          {" — "}
          {item ? item.name : required ? t("No disponible en tu cuenta") : t("Opcional, no está en tu cuenta")}
        </span>
      </span>
    </li>
  );
}
